/**
 * Vidyard metadata helpers — AQSN-172.
 *
 * Vidyard's `renderPlayer({ type: 'background' })` is not a real embed type
 * (only 'inline' and 'lightbox' exist); the SDK silently falls back to
 * 'inline', where the built-in muted-autoplay "Click to unmute" overlay
 * cannot be suppressed via config because it renders inside a cross-origin
 * iframe. The reliable chromeless embed is a native <video> element sourced
 * directly from the resolved MP4 URL below, rendered via the existing
 * BackgroundVideo component.
 */

const VIDYARD_MP4_PROFILE_PREFERENCE = ['1080p', '720p', '480p', '360p'] as const;

interface VidyardMp4Source {
    profile: string;
    url: string;
}

interface VidyardMetadataResponse {
    payload?: {
        chapters?: Array<{
            sources?: {
                mp4?: VidyardMp4Source[];
            };
        }>;
    };
}

/**
 * Pick the best available MP4 source URL from Vidyard's resolution profiles,
 * preferring higher resolutions and falling back to whatever is available.
 */
export function pickBestMp4Source(sources: VidyardMp4Source[]): string | null {
    if (!sources.length) return null;

    for (const profile of VIDYARD_MP4_PROFILE_PREFERENCE) {
        const match = sources.find((source) => source.profile === profile);
        if (match) return match.url;
    }

    return sources[0].url;
}

/**
 * Resolve a direct, playable MP4 URL for a Vidyard video via its public
 * metadata endpoint. Returns null on any failure (network error, non-OK
 * response, missing sources) so callers can gracefully degrade to a static
 * poster rather than crash.
 */
export async function getVidyardMp4Url(vidyardId: string): Promise<string | null> {
    if (!vidyardId) return null;

    try {
        const response = await fetch(`https://play.vidyard.com/player/${vidyardId}.json`, {
            next: { revalidate: 3600 },
        });
        if (!response.ok) return null;

        const data = (await response.json()) as VidyardMetadataResponse;
        const sources = data.payload?.chapters?.[0]?.sources?.mp4;
        if (!sources?.length) return null;

        return pickBestMp4Source(sources);
    } catch {
        return null;
    }
}
